import { IconSparkles } from '@tabler/icons-react'
import styled, { keyframes } from 'styled-components'

import { ButtonBase, ButtonProps } from './BUI/Button'

import { buildPassTheme } from 'src/styles/theme'

const Wrapper = styled.div`
  width: fit-content;
  height: fit-content;
`

const ButtonWrapper = styled.div`
  position: relative;
  width: fit-content;
  height: fit-content;
`

const PrimaryButton = styled(ButtonBase)`
  position: relative;
  background-color: ${({ theme }) => theme.colour.ai.light};
  border-color: ${({ theme }) => theme.colour.ai.light};
  color: ${({ theme }) => theme.colour.secondary.base};

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colour.ai.dark};
    border-color: ${({ theme }) => theme.colour.ai.dark};
    color: ${({ theme }) => theme.colour.neutral.white};
  }
`

const SecondaryButton = styled(ButtonBase)`
  position: relative;
  background-color: ${({ theme }) => theme.colour.neutral.white};
  border-color: ${({ theme }) => theme.colour.ai.light};
  color: ${({ theme }) => theme.colour.ai.dark};

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colour.ai.light};
    border-color: ${({ theme }) => theme.colour.ai.dark};
    color: ${({ theme }) => theme.colour.ai.dark};
  }
`

const Spin = keyframes`
	100% {
		transform: rotate(1turn);
  }
`

const COLOUR_A = buildPassTheme.colour.ai.animationColourA
const COLOUR_B = buildPassTheme.colour.ai.animationColourB

export const AnimatedWrapper = styled.div`
  position: absolute;
  z-index: 0;
  width: calc(100% + 0.5rem);
  height: calc(100% + 0.5rem);
  border-radius: 10px;
  overflow: hidden;
  transform: translate(-0.25rem, -0.25rem);
  user-select: none;

  &::before {
    content: '';
    position: absolute;
    z-index: -2;
    left: -100%;
    top: -200%;
    width: 300%;
    height: 500%;
    background-color: ${COLOUR_A};
    background-repeat: no-repeat;
    background-size:
      50% 50%,
      50% 50%;
    background-position:
      0 0,
      100% 0,
      100% 100%,
      0 100%;
    background-image: linear-gradient(${COLOUR_A}, ${COLOUR_A}),
      linear-gradient(#ffffff, #ffffff),
      linear-gradient(${COLOUR_B}, ${COLOUR_B}),
      linear-gradient(#ffffff, #ffffff);
    animation: ${Spin} 2s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    z-index: -1;
    left: 6px;
    top: 6px;
    width: calc(100% - 12px);
    height: calc(100% - 12px);
    border-radius: 5px;
  }
`

type Props = ButtonProps & {
  loadingText?: string
  useAnimation?: boolean
  variant?: 'primary' | 'secondary'
}

export const AIButton: React.FC<Props> = ({
  children,
  icon,
  loading,
  disabled,
  loadingText = 'Generating...',
  useAnimation = true,
  variant = 'primary',
  ...buttonProps
}) => {
  let Button

  switch (variant) {
    case 'primary':
      Button = PrimaryButton
      break
    case 'secondary':
      Button = SecondaryButton
      break
    default:
      Button = PrimaryButton
      break
  }

  return (
    <Wrapper>
      <ButtonWrapper>
        {loading && useAnimation && <AnimatedWrapper />}
        <Button {...buttonProps} disabled={loading || disabled}>
          {icon ?? <IconSparkles size={16} />}
          {loading ? loadingText : children}
        </Button>
      </ButtonWrapper>
    </Wrapper>
  )
}
